<script>
import { mapGetters } from 'vuex';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { useAlert } from 'dashboard/composables';
import SchedulingPagesAPI from 'dashboard/api/schedulingPages';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import googleCalendarAPI from '../../../api/googleCalendar';
import calendarAPI from '../../../api/calendar';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WootModal from 'dashboard/components/Modal.vue';
import DatePicker from 'vue-datepicker-next';
import { DATE_PICKER_LANG_PT_BR } from 'dashboard/helper/datePickerLang';

export default {
  components: {
    FullCalendar,
    NextButton,
    WootModal,
    DatePicker,
  },
  data() {
    return {
      datePickerLang: DATE_PICKER_LANG_PT_BR,
      isLoadingEvents: false,
      hasLoadedEvents: false,
      // Duração padrão de reunião = regra da agenda da conta (fallback 30)
      defaultDurationMinutes: 30,
      currentTitle: '',
      currentView: 'timeGridWeek',
      showEventModal: false,
      isNewEvent: false,
      eventForm: {
        title: '',
        start: '',
        end: '',
        description: '',
        syncWithGoogle: true,
        conversationId: null,
        meeting_provider: 'chatsuite',
        location: '',
        participants: [],
        invite_inbox_id: null,
        invite_template_name: null,
        confirmationEnabled: true,
      },
      selectedEvent: null,
      isGoogleConnected: false,
      isSyncing: false,
      isSaving: false,
      statusLegend: [
        { id: 'scheduled', name: 'Agendado', color: '#6366f1' },
        { id: 'confirmed', name: 'Confirmado', color: '#10b981' },
        { id: 'google', name: 'Google Calendar', color: '#4285F4' },
        { id: 'completed', name: 'Concluído', color: '#64748b' },
        { id: 'cancelled', name: 'Cancelado', color: '#ef4444' },
        { id: 'no_show', name: 'Não Compareceu', color: '#f97316' },
      ],
      googleEmail: null,
      googleCalendars: [],
      // Todos os agentes da conta + quem tem Google conectado (mesma fonte do
      // "Agendar para" dos Agendamentos), pra escolher a agenda do evento.
      schedulableAgents: [],
      selectedAgent: '',
      selectedType: '',
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
        initialView: 'timeGridWeek',
        locale: ptBrLocale,
        headerToolbar: false,
        height: '100%',
        editable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: 3,
        weekends: true,
        events: this.fetchEvents,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDrop,
        eventResize: this.handleEventResize,
        datesSet: this.handleDatesSet,
        slotMinTime: '06:00:00',
        // 24h (28/08, pedido Ivan): a grade cortava às 21h mesmo com
        // disponibilidade configurada até meia-noite.
        slotMaxTime: '24:00:00',
        nowIndicator: true,
        allDayText: '',
        navLinks: true,
        businessHours: {
          daysOfWeek: [1, 2, 3, 4, 5],
          startTime: '09:00',
          endTime: '18:00',
        },
        buttonText: {
          today: 'Hoje',
          month: 'Mês',
          week: 'Semana',
          day: 'Dia',
          list: 'Lista',
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      whatsappInboxes: 'inboxes/getWhatsAppInboxes',
    }),
    hasWhatsappGuest() {
      return (this.eventForm.participants || []).some(p =>
        (p.via || []).includes('whatsapp')
      );
    },
    selectedInviteInbox() {
      return (this.whatsappInboxes || []).find(
        i => i.id === this.eventForm.invite_inbox_id
      );
    },
    // Cloud API/360dialog: fora da janela de 24h só entrega template aprovado.
    isOfficialInviteInbox() {
      return ['whatsapp_cloud', 'default'].includes(
        this.selectedInviteInbox?.provider
      );
    },
    inviteTemplates() {
      return (this.selectedInviteInbox?.message_templates || []).filter(
        t => String(t.status).toLowerCase() === 'approved'
      );
    },
    selectedInviteTemplateBody() {
      const tpl = this.inviteTemplates.find(
        t => t.name === this.eventForm.invite_template_name
      );
      const body = (tpl?.components || []).find(
        c => String(c.type).toLowerCase() === 'body'
      );
      return body?.text || '';
    },
    calendarApi() {
      return this.$refs.fullCalendar?.getApi();
    },

    // Agenda escolhida no modal está sem Google: a reunião fica só no ChatSuite.
    agendaOwnerWithoutGoogle() {
      const owner = this.schedulableAgents.find(
        a => Number(a.id) === Number(this.eventForm.assigned_to_id)
      );
      return Boolean(owner) && !owner.google_connected;
    },

    accountDefaultProvider() {
      return (
        this.$store.getters.getCurrentAccount?.settings
          ?.default_meeting_provider || 'chatsuite'
      );
    },
    isAdmin() {
      return this.$store.getters.getCurrentRole === 'administrator';
    },
    // SDR (calendar_manage) também vê todas as agendas e o filtro por agente.
    canViewAllCalendars() {
      if (this.isAdmin) return true;
      const user = this.$store.getters.getCurrentUser;
      const accountId = this.$store.getters.getCurrentAccountId;
      const accountEntry = (user?.accounts || []).find(a => a.id === accountId);
      return (accountEntry?.permissions || []).includes('calendar_manage');
    },
    viewOptions() {
      return [
        { label: this.$t('CALENDAR.MONTH'), value: 'dayGridMonth' },
        { label: this.$t('CALENDAR.WEEK'), value: 'timeGridWeek' },
        { label: this.$t('CALENDAR.DAY'), value: 'timeGridDay' },
        { label: this.$t('CALENDAR.LIST'), value: 'listWeek' },
      ];
    },
  },
  mounted() {
    this.loadGoogleCalendarStatus();
    this.loadAgents();
    this.loadDefaultDuration();
    if (this.canViewAllCalendars) this.loadSchedulableAgents();
  },
  methods: {
    async loadDefaultDuration() {
      try {
        const { data } = await SchedulingPagesAPI.defaults();
        if (data?.duration_minutes > 0)
          this.defaultDurationMinutes = data.duration_minutes;
      } catch {
        // sem regra de agenda: mantém 30
      }
    },
    async fetchEvents(fetchInfo, successCallback) {
      // Overlay só no PRIMEIRO carregamento — refetch silencioso não pisca.
      if (!this.hasLoadedEvents) this.isLoadingEvents = true;
      try {
        const params = {
          start: fetchInfo.startStr,
          end: fetchInfo.endStr,
          type: this.selectedType,
          agent_id: this.selectedAgent,
        };

        const { data } = await calendarAPI.getEvents(params);
        successCallback(data || []);
      } catch {
        successCallback([]);
      } finally {
        this.isLoadingEvents = false;
        this.hasLoadedEvents = true;
      }
    },

    async loadGoogleCalendarStatus() {
      try {
        const { data } = await googleCalendarAPI.getStatus();
        this.isGoogleConnected = data.connected;
        this.googleEmail = data.email;

        if (this.isGoogleConnected) {
          await this.loadGoogleCalendars();
        }
      } catch (error) {
        // Silently fail
      }
    },

    async loadGoogleCalendars() {
      try {
        const { data } = await googleCalendarAPI.listCalendars();
        this.googleCalendars = data.calendars.map(cal => ({
          ...cal,
          visible: false,
        }));
      } catch (error) {
        // Silently fail
      }
    },

    async loadAgents() {
      try {
        await this.$store.dispatch('agents/get');
      } catch (error) {
        // Silently fail
      }
    },

    async loadSchedulableAgents() {
      try {
        const { data } = await googleCalendarAPI.listSchedulableAgents();
        this.schedulableAgents = data.users || [];
      } catch (error) {
        // Sem a lista, o modal simplesmente não oferece a escolha.
      }
    },

    currentUserId() {
      return this.$store.getters.getCurrentUser?.id || null;
    },

    refreshCalendar() {
      this.calendarApi?.refetchEvents();
    },

    previousPeriod() {
      this.calendarApi?.prev();
    },

    nextPeriod() {
      this.calendarApi?.next();
    },

    goToToday() {
      this.calendarApi?.today();
    },

    changeView(viewType) {
      this.calendarApi?.changeView(viewType);
      this.currentView = viewType;
    },

    handleDatesSet(dateInfo) {
      this.currentTitle = dateInfo.view.title;
      this.currentView = dateInfo.view.type;
    },

    handleDateSelect(selectInfo) {
      this.isNewEvent = true;
      // Clique simples seleciona 1 slot de 30min do FullCalendar — o evento
      // deve nascer com a DURAÇÃO DA REGRA DA AGENDA (28/08). Arrastar um
      // intervalo maior continua valendo o que foi arrastado.
      let end = selectInfo.end;
      const selecionadoMs = selectInfo.end - selectInfo.start;
      if (selecionadoMs <= 30 * 60000) {
        end = new Date(
          selectInfo.start.getTime() + this.defaultDurationMinutes * 60000
        );
      }
      this.eventForm = {
        title: '',
        start: this.formatDateTimeLocal(selectInfo.start),
        end: this.formatDateTimeLocal(end),
        description: '',
        syncWithGoogle: this.isGoogleConnected,
        meeting_provider: this.accountDefaultProvider,
        location: '',
        participants: [],
        invite_inbox_id: null,
        invite_template_name: null,
        confirmationEnabled: true,
        assigned_to_id: this.currentUserId(),
      };
      this.selectedEvent = selectInfo;
      this.showEventModal = true;
      this.calendarApi?.unselect();
    },

    handleEventClick(clickInfo) {
      const event = clickInfo.event;

      this.isNewEvent = false;
      this.eventForm = {
        id: event.id,
        title: event.title,
        start: this.formatDateTimeLocal(event.start),
        end: this.formatDateTimeLocal(event.end),
        description: event.extendedProps.description || '',
        syncWithGoogle: event.extendedProps.synced_with_google || false,
        meeting_provider: event.extendedProps.meeting_provider || 'chatsuite',
        location: event.extendedProps.location || '',
        chatsuite_host_url: event.extendedProps.chatsuite_host_url || '',
        chatsuite_guest_url: event.extendedProps.chatsuite_guest_url || '',
        participants: (event.extendedProps.participants || []).map(p => ({
          ...p,
          via: [...(p.via || [])],
        })),
        notifyClient: event.extendedProps.notify_client !== false,
        status: event.extendedProps.status || 'scheduled',
        invite_inbox_id: event.extendedProps.invite_inbox_id || null,
        invite_template_name: event.extendedProps.invite_template_name || null,
        confirmationEnabled: event.extendedProps.confirmation_enabled !== false,
        contactName: event.extendedProps.contact_name || '',
        hasConversation: Boolean(event.extendedProps.conversation_id),
        // Dono da agenda: cai no agent_id (que já vem como dono) quando o
        // evento é antigo e não tem assigned_to gravado.
        assigned_to_id:
          event.extendedProps.assigned_to_id ||
          event.extendedProps.agent_id ||
          null,
      };
      this.selectedEvent = event;
      this.showEventModal = true;
    },

    async handleEventDrop(dropInfo) {
      try {
        useAlert(this.$t('CALENDAR.EVENT_UPDATED'));
      } catch (error) {
        dropInfo.revert();
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      }
    },

    async handleEventResize(resizeInfo) {
      try {
        useAlert(this.$t('CALENDAR.EVENT_UPDATED'));
      } catch (error) {
        resizeInfo.revert();
        useAlert(this.$t('CALENDAR.ERRORS.UPDATE_FAILED'));
      }
    },

    createNewEvent() {
      this.isNewEvent = true;
      this.eventForm = {
        title: '',
        start: this.formatDateTimeLocal(new Date()),
        end: this.formatDateTimeLocal(
          new Date(Date.now() + this.defaultDurationMinutes * 60000)
        ),
        description: '',
        syncWithGoogle: this.isGoogleConnected,
        meeting_provider: this.accountDefaultProvider,
        location: '',
        participants: [],
        invite_inbox_id: null,
        invite_template_name: null,
        confirmationEnabled: true,
        assigned_to_id: this.currentUserId(),
      };
      this.showEventModal = true;
    },

    async saveEvent() {
      if (!this.eventForm.title) {
        useAlert(this.$t('CALENDAR.ERRORS.TITLE_REQUIRED'));
        return;
      }

      this.isSaving = true;
      try {
        const eventData = {
          title: this.eventForm.title,
          description: this.eventForm.description,
          scheduled_at: new Date(this.eventForm.start).toISOString(),
          duration_minutes: this.calculateDurationMinutes(),
          synced_with_google: this.eventForm.syncWithGoogle,
          conversation_id: this.eventForm.conversationId,
          meeting_provider: this.eventForm.meeting_provider,
          location: this.eventForm.location,
          participants: this.participantsPayload(),
          invite_inbox_id: this.eventForm.invite_inbox_id || null,
          invite_template_name: this.eventForm.invite_template_name || null,
          notify_client: this.eventForm.notifyClient !== false,
          confirmation_enabled: this.eventForm.confirmationEnabled !== false,
          assigned_to_id: this.eventForm.assigned_to_id || null,
        };

        if (this.isNewEvent) {
          await calendarAPI.createEvent(eventData);
          useAlert(this.$t('CALENDAR.EVENT_CREATED'));
        } else {
          await calendarAPI.updateEvent(this.eventForm.id, eventData);
          useAlert(this.$t('CALENDAR.EVENT_UPDATED'));
        }
        this.closeEventModal();
        this.refreshCalendar();
      } catch (error) {
        useAlert(this.$t('CALENDAR.ERRORS.SAVE_FAILED'));
      } finally {
        this.isSaving = false;
      }
    },

    async deleteEvent() {
      try {
        await calendarAPI.deleteEvent(this.eventForm.id);
        useAlert(this.$t('CALENDAR.EVENT_DELETED'));
        this.closeEventModal();
        this.refreshCalendar();
      } catch (error) {
        useAlert(this.$t('CALENDAR.ERRORS.DELETE_FAILED'));
      }
    },

    // Convidados: só vai pro backend quem tem WhatsApp ou email preenchido.
    participantsPayload() {
      return (this.eventForm.participants || [])
        .filter(p => (p.phone || '').trim() || (p.email || '').trim())
        .map(p => ({
          name: (p.name || '').trim(),
          phone: (p.phone || '').trim(),
          email: (p.email || '').trim(),
          via: p.via && p.via.length ? p.via : undefined,
        }));
    },

    addParticipant() {
      this.eventForm.participants.push({
        name: '',
        phone: '',
        email: '',
        via: [],
      });
    },

    // Chip de equipe: convidado já preenchido com o WhatsApp/email do perfil
    // (Perfil → WhatsApp). Clique de novo remove.
    toggleTeamParticipant(agent) {
      const email = (agent.email || '').toLowerCase();
      const existing = this.eventForm.participants.findIndex(
        p => (p.email || '').toLowerCase() === email && email
      );
      if (existing >= 0) {
        this.eventForm.participants.splice(existing, 1);
        return;
      }
      const phone = agent.custom_attributes?.phone_number || '';
      this.eventForm.participants.push({
        name: agent.name || '',
        phone,
        email: agent.email || '',
        via: phone ? ['whatsapp', 'email'] : ['email'],
      });
    },

    isTeamParticipant(agent) {
      const email = (agent.email || '').toLowerCase();
      return (
        !!email &&
        (this.eventForm.participants || []).some(
          p => (p.email || '').toLowerCase() === email
        )
      );
    },

    removeParticipant(index) {
      this.eventForm.participants.splice(index, 1);
    },

    toggleParticipantVia(participant, channel) {
      const idx = participant.via.indexOf(channel);
      if (idx >= 0) participant.via.splice(idx, 1);
      else participant.via.push(channel);
    },

    calculateDurationMinutes() {
      if (!this.eventForm.start || !this.eventForm.end) return 30;
      const start = new Date(this.eventForm.start);
      const end = new Date(this.eventForm.end);
      return Math.round((end - start) / 60000);
    },

    closeEventModal() {
      this.showEventModal = false;
      this.selectedEvent = null;
      this.isSaving = false;
      this.eventForm = {
        title: '',
        start: '',
        end: '',
        description: '',
        syncWithGoogle: true,
        conversationId: null,
        participants: [],
      };
    },

    // Abre a sala já como anfitrião (grava/modera/encerra) via host_url (#host=token).
    joinAsHost() {
      const url = this.eventForm.chatsuite_host_url;
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    async copyGuestLink() {
      const url =
        this.eventForm.chatsuite_guest_url || this.eventForm.chatsuite_host_url;
      if (!url) return;
      try {
        await copyTextToClipboard(url);
        useAlert(this.$t('CALENDAR.GUEST_LINK_COPIED'));
      } catch (error) {
        useAlert(this.$t('CALENDAR.GUEST_LINK_COPY_ERROR'));
      }
    },

    async syncWithGoogle() {
      this.isSyncing = true;
      try {
        await googleCalendarAPI.sync();
        useAlert(this.$t('CALENDAR.SYNC_SUCCESS'));
        this.refreshCalendar();
      } catch (error) {
        useAlert(this.$t('CALENDAR.ERRORS.SYNC_FAILED'));
      } finally {
        this.isSyncing = false;
      }
    },

    async disconnectGoogle() {
      try {
        await googleCalendarAPI.disconnect();
        this.isGoogleConnected = false;
        this.googleEmail = null;
        this.googleCalendars = [];
        useAlert(this.$t('CALENDAR.DISCONNECT'));
        this.refreshCalendar();
      } catch {
        useAlert(this.$t('CALENDAR.ERRORS.DISCONNECT_FAILED'));
      }
    },

    goToIntegrations() {
      this.$router.push({
        name: 'settings_integrations_google_calendar',
      });
    },

    formatDateTimeLocal(date) {
      if (!date) return '';
      const d = new Date(date);
      const offset = d.getTimezoneOffset() * 60000;
      const localDate = new Date(d.getTime() - offset);
      return localDate.toISOString().slice(0, 16);
    },

    toDateObj(value) {
      return value ? new Date(value) : null;
    },
  },
};
</script>

<template>
  <section
    class="calendar-page flex flex-col h-full w-full min-w-0 bg-n-background"
  >
    <!-- Header -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-n-weak bg-n-background"
    >
      <div class="flex items-center gap-3 min-w-0">
        <!-- New Event Button (Google-style prominent "Criar") -->
        <NextButton
          color="blue"
          size="sm"
          data-tour="agenda-novo"
          @click="createNewEvent"
        >
          <div class="flex items-center gap-1.5">
            <span class="i-lucide-plus size-4" />
            {{ $t('CALENDAR.NEW_EVENT') }}
          </div>
        </NextButton>

        <div class="flex items-center gap-1">
          <NextButton
            icon="i-lucide-chevron-left"
            size="sm"
            variant="ghost"
            color="slate"
            @click="previousPeriod"
          />

          <NextButton
            size="sm"
            variant="outline"
            color="slate"
            @click="goToToday"
          >
            {{ $t('CALENDAR.TODAY') }}
          </NextButton>

          <NextButton
            icon="i-lucide-chevron-right"
            size="sm"
            variant="ghost"
            color="slate"
            @click="nextPeriod"
          />
        </div>

        <h1 class="text-xl font-medium text-n-slate-12 truncate capitalize">
          {{ currentTitle }}
        </h1>
      </div>

      <div class="flex items-center gap-3 flex-wrap">
        <!-- Pipeline View Switcher (Kanban/Calendar) -->
        <div class="flex items-center gap-1 bg-n-solid-2 p-0.5 rounded-lg">
          <router-link
            :to="{ name: 'pipeline_view' }"
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
            :title="$t('CALENDAR.KANBAN_VIEW')"
          >
            <span class="i-lucide-columns-3 size-4" />
            <span class="hidden sm:inline">{{
              $t('CALENDAR.KANBAN_VIEW')
            }}</span>
          </router-link>
          <button
            type="button"
            class="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors bg-n-solid-1 text-n-slate-12 shadow-sm"
            :title="$t('CALENDAR.TITLE')"
          >
            <span class="i-lucide-calendar size-4" />
            <span class="hidden sm:inline">{{ $t('CALENDAR.TITLE') }}</span>
          </button>
        </div>

        <!-- View Switcher -->
        <div
          class="inline-flex rounded-lg border border-n-weak p-1 bg-n-solid-2"
        >
          <button
            v-for="view in viewOptions"
            :key="view.value"
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="[
              currentView === view.value
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:bg-n-alpha-2',
            ]"
            @click="changeView(view.value)"
          >
            {{ view.label }}
          </button>
        </div>

        <!-- Google Sync Button -->
        <NextButton
          v-if="isGoogleConnected"
          size="sm"
          variant="outline"
          color="slate"
          :is-loading="isSyncing"
          @click="syncWithGoogle"
        >
          <div class="flex items-center gap-2">
            <span
              class="i-lucide-refresh-cw size-4"
              :class="{ 'animate-spin': isSyncing }"
            />
            {{ $t('CALENDAR.SYNC') }}
          </div>
        </NextButton>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Sidebar Filters -->
      <div
        class="w-56 flex-shrink-0 border-r border-n-weak bg-n-solid-1 p-4 overflow-y-auto"
      >
        <!-- Calendar Sources -->
        <div class="mb-6">
          <h3 class="text-sm font-semibold text-n-slate-11 mb-3">
            {{ $t('CALENDAR.MY_CALENDARS') }}
          </h3>

          <div class="space-y-1">
            <div
              v-for="status in statusLegend"
              :key="status.id"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            >
              <span
                class="w-3 h-3 rounded-full"
                :style="{ backgroundColor: status.color }"
              />
              <span class="text-xs text-n-slate-10">{{ status.name }}</span>
            </div>
          </div>
        </div>

        <!-- Google Calendar Status -->
        <div v-if="isGoogleConnected" class="mb-6">
          <div class="flex items-center gap-2 mb-3">
            <svg class="w-4 h-4" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <h3 class="text-sm font-semibold text-n-slate-11">
              {{ $t('CALENDAR.GOOGLE_CALENDAR') }}
            </h3>
          </div>
          <div
            class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-3"
          >
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-green-500" />
              <span class="text-xs text-green-700 dark:text-green-400">{{
                $t('CALENDAR.CONNECTED')
              }}</span>
            </div>
            <p
              class="text-xs text-n-slate-10 mt-1 truncate"
              :title="googleEmail"
            >
              {{ googleEmail }}
            </p>
          </div>
          <button
            class="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 transition-colors"
            @click="disconnectGoogle"
          >
            {{ $t('CALENDAR.DISCONNECT') }}
          </button>
        </div>

        <!-- Filters -->
        <div class="mb-6">
          <h3 class="text-sm font-semibold text-n-slate-11 mb-3">
            {{ $t('CALENDAR.FILTERS') }}
          </h3>

          <div class="space-y-3">
            <div v-if="canViewAllCalendars">
              <label class="block text-xs font-medium text-n-slate-10 mb-1">
                {{ $t('CALENDAR.FILTER_BY_AGENT') }}
              </label>
              <select
                v-model="selectedAgent"
                class="form-select text-sm w-full"
                @change="refreshCalendar"
              >
                <option value="">
                  {{ $t('CALENDAR.ALL_AGENTS') }}
                </option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-10 mb-1">
                {{ $t('CALENDAR.FILTER_BY_TYPE') }}
              </label>
              <select
                v-model="selectedType"
                class="form-select text-sm w-full"
                @change="refreshCalendar"
              >
                <option value="">
                  {{ $t('CALENDAR.ALL_TYPES') }}
                </option>
                <option value="appointments">
                  {{ $t('CALENDAR.APPOINTMENTS') }}
                </option>
                <option value="tasks">
                  {{ $t('CALENDAR.TASKS') }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Google Calendar Connect -->
        <div
          v-if="!isGoogleConnected"
          class="bg-n-alpha-2 rounded-xl p-4 border border-n-weak"
        >
          <div class="flex flex-col items-center text-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-n-solid-1 flex items-center justify-center shadow-sm"
            >
              <svg class="w-6 h-6" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </div>
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">
                {{ $t('CALENDAR.GOOGLE_CALENDAR') }}
              </h4>
              <p class="text-xs text-n-slate-10 mb-3">
                {{
                  $t('CALENDAR.CONNECT_GOOGLE_DESC') ||
                  'Sincronize seus eventos'
                }}
              </p>
            </div>
            <button
              class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-n-solid-1 hover:bg-n-alpha-2 text-n-slate-11 text-sm font-medium rounded-lg border border-n-weak shadow-sm transition-all hover:shadow"
              @click="goToIntegrations"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {{ $t('CALENDAR.CONNECT') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Calendar -->
      <div
        class="relative flex-1 p-4 overflow-hidden bg-n-background calendar-container min-w-0"
      >
        <div
          v-if="isLoadingEvents"
          class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-n-background/80 backdrop-blur-sm"
        >
          <div
            class="h-10 w-10 animate-spin rounded-full border-[3px] border-n-alpha-2 border-t-n-brand"
          />
          <p class="text-sm font-medium text-n-slate-11">
            {{ $t('CALENDAR.LOADING_EVENTS') }}
          </p>
        </div>
        <FullCalendar
          ref="fullCalendar"
          class="h-full w-full"
          :options="calendarOptions"
        />
      </div>
    </div>

    <!-- Event Modal -->
    <WootModal
      v-if="showEventModal"
      v-model:show="showEventModal"
      :on-close="closeEventModal"
      size="!w-[30rem]"
    >
      <div class="p-6">
        <h2
          class="flex items-center gap-2 text-lg font-semibold mb-5 text-n-slate-12"
        >
          <span class="i-lucide-calendar-plus size-5 text-n-brand" />
          {{
            isNewEvent ? $t('CALENDAR.NEW_EVENT') : $t('CALENDAR.EDIT_EVENT')
          }}
        </h2>
        <div
          v-if="!isNewEvent && eventForm.status === 'confirmed'"
          class="mb-4 inline-flex items-center gap-1.5 rounded-full bg-n-teal-3 px-3 py-1 text-xs font-semibold text-n-teal-11"
        >
          <span class="i-lucide-circle-check size-3.5" />
          {{ $t('CALENDAR.ATTENDANCE_CONFIRMED') }}
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1 text-n-slate-11">
              {{ $t('CALENDAR.EVENT_TITLE') }}
            </label>
            <input
              v-model="eventForm.title"
              type="text"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-xs text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="$t('CALENDAR.EVENT_TITLE_PLACEHOLDER')"
            />
          </div>

          <!-- Agenda de destino: o SDR marca na agenda do closer. Só quem pode
               ver todas as agendas escolhe (o backend confere de novo). -->
          <div v-if="canViewAllCalendars">
            <label class="block text-sm font-medium mb-1 text-n-slate-11">
              {{ $t('CALENDAR.AGENDA_OWNER') }}
            </label>
            <select
              v-model="eventForm.assigned_to_id"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-xs text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option
                v-for="agendaAgent in schedulableAgents"
                :key="agendaAgent.id"
                :value="agendaAgent.id"
              >
                {{ agendaAgent.name }}
                {{
                  agendaAgent.google_connected
                    ? ''
                    : $t('CALENDAR.AGENDA_OWNER_NO_GOOGLE')
                }}
              </option>
            </select>
            <p
              v-if="agendaOwnerWithoutGoogle"
              class="mt-1 text-xs text-n-amber-11"
            >
              {{ $t('CALENDAR.AGENDA_OWNER_NO_GOOGLE_HINT') }}
            </p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1 text-n-slate-11">
                {{ $t('CALENDAR.START_TIME') }}
              </label>
              <div class="date-picker no-margin auto-width">
                <DatePicker
                  type="datetime"
                  input-class="mx-input !text-sm"
                  :value="toDateObj(eventForm.start)"
                  format="DD/MM/YYYY HH:mm"
                  :minute-step="5"
                  :clearable="false"
                  :editable="false"
                  confirm
                  confirm-text="OK"
                  :lang="datePickerLang"
                  @change="
                    v => (eventForm.start = v ? formatDateTimeLocal(v) : '')
                  "
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1 text-n-slate-11">
                {{ $t('CALENDAR.END_TIME') }}
              </label>
              <div class="date-picker no-margin auto-width">
                <DatePicker
                  type="datetime"
                  input-class="mx-input !text-sm"
                  :value="toDateObj(eventForm.end)"
                  format="DD/MM/YYYY HH:mm"
                  :minute-step="5"
                  :clearable="false"
                  :editable="false"
                  confirm
                  confirm-text="OK"
                  :lang="datePickerLang"
                  @change="
                    v => (eventForm.end = v ? formatDateTimeLocal(v) : '')
                  "
                />
              </div>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1 text-n-slate-11">
              {{ $t('CALENDAR.DESCRIPTION') }}
            </label>
            <textarea
              v-model="eventForm.description"
              rows="3"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="$t('CALENDAR.DESCRIPTION_PLACEHOLDER')"
            />
          </div>

          <!-- Link da reunião -->
          <div>
            <label class="block text-sm font-medium mb-1 text-n-slate-11">
              {{ $t('APPOINTMENTS.MEETING_LINK.LABEL') }}
            </label>
            <div class="flex gap-2">
              <button
                type="button"
                class="flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-all"
                :class="
                  eventForm.meeting_provider === 'chatsuite'
                    ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                    : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
                "
                @click="eventForm.meeting_provider = 'chatsuite'"
              >
                <span class="i-lucide-video size-4" />
                {{ $t('APPOINTMENTS.MEETING_LINK.CHATSUITE') }}
              </button>
              <button
                type="button"
                :disabled="!isGoogleConnected"
                class="flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                :class="
                  eventForm.meeting_provider === 'google_meet'
                    ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                    : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
                "
                @click="
                  isGoogleConnected &&
                  (eventForm.meeting_provider = 'google_meet')
                "
              >
                <span class="i-lucide-monitor size-4" />
                {{ $t('APPOINTMENTS.MEETING_LINK.GOOGLE_MEET') }}
              </button>
              <button
                type="button"
                class="flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-all"
                :class="
                  eventForm.meeting_provider === 'presential'
                    ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                    : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
                "
                @click="eventForm.meeting_provider = 'presential'"
              >
                <span class="i-lucide-map-pin size-4" />
                {{ $t('APPOINTMENTS.MEETING_LINK.PRESENTIAL') }}
              </button>
            </div>
            <p
              v-if="
                !isGoogleConnected &&
                eventForm.meeting_provider !== 'presential'
              "
              class="mt-1 text-xs text-n-slate-10"
            >
              {{ $t('APPOINTMENTS.MEETING_LINK.CONNECT_HINT') }}
            </p>
            <input
              v-if="eventForm.meeting_provider === 'presential'"
              v-model="eventForm.location"
              type="text"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-sm text-n-slate-12 outline-none focus:border-n-brand mt-2"
              :placeholder="$t('APPOINTMENTS.MEETING_LINK.LOCATION_PH')"
            />
          </div>

          <!-- Convidados: cada um recebe convite + lembrete no canal marcado -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('CALENDAR.PARTICIPANTS.TITLE') }}
              </label>
              <button
                type="button"
                class="flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:text-n-blue-12"
                @click="addParticipant"
              >
                <span class="i-lucide-plus size-3.5" />
                {{ $t('CALENDAR.PARTICIPANTS.ADD') }}
              </button>
            </div>
            <div
              v-if="agents.length"
              class="flex flex-wrap items-center gap-1.5"
            >
              <span class="text-[11px] text-n-slate-10">
                {{ $t('CALENDAR.PARTICIPANTS.TEAM_HINT') }}
              </span>
              <button
                v-for="teamAgent in agents"
                :key="teamAgent.id"
                type="button"
                class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                :class="
                  isTeamParticipant(teamAgent)
                    ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                    : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
                "
                @click="toggleTeamParticipant(teamAgent)"
              >
                {{ teamAgent.name }}
              </button>
            </div>
            <p
              v-if="eventForm.participants.length"
              class="text-xs text-n-slate-10"
            >
              {{ $t('CALENDAR.PARTICIPANTS.HINT') }}
            </p>
            <!-- Caixa que envia o convite/lembrete no WhatsApp (oficial exige
                 template — o cliente escolhe por onde sai) -->
            <div
              v-if="
                (hasWhatsappGuest || eventForm.hasConversation) &&
                whatsappInboxes.length
              "
              class="flex flex-col gap-1"
            >
              <label class="text-xs font-medium text-n-slate-11">
                {{ $t('CALENDAR.PARTICIPANTS.INBOX_LABEL') }}
              </label>
              <select
                v-model="eventForm.invite_inbox_id"
                class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option :value="null">
                  {{ $t('CALENDAR.PARTICIPANTS.INBOX_DEFAULT') }}
                </option>
                <option
                  v-for="ibx in whatsappInboxes"
                  :key="ibx.id"
                  :value="ibx.id"
                >
                  {{ ibx.name }}
                </option>
              </select>
              <template v-if="isOfficialInviteInbox">
                <label class="mt-1 text-xs font-medium text-n-slate-11">
                  {{ $t('CALENDAR.PARTICIPANTS.TEMPLATE_LABEL') }}
                </label>
                <select
                  v-model="eventForm.invite_template_name"
                  class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                >
                  <option :value="null">
                    {{ $t('CALENDAR.PARTICIPANTS.TEMPLATE_NONE') }}
                  </option>
                  <option
                    v-for="tpl in inviteTemplates"
                    :key="tpl.name + tpl.language"
                    :value="tpl.name"
                  >
                    {{ tpl.name }}
                  </option>
                </select>
                <p
                  v-if="!eventForm.invite_template_name"
                  class="text-[11px] text-n-amber-11"
                >
                  {{ $t('CALENDAR.PARTICIPANTS.TEMPLATE_WARNING') }}
                </p>
                <p v-else class="text-[11px] text-n-slate-10">
                  {{ $t('CALENDAR.PARTICIPANTS.TEMPLATE_VARS_HINT') }}
                </p>
                <!-- Preview do template: o que o convidado vai receber -->
                <div
                  v-if="selectedInviteTemplateBody"
                  class="whitespace-pre-wrap rounded-lg border border-n-weak bg-n-solid-1 p-2.5 text-xs text-n-slate-11"
                >
                  {{ selectedInviteTemplateBody }}
                </div>
              </template>
            </div>
            <!-- Cliente do evento: desmarcar = reunião interna, cliente não recebe nada -->
            <button
              v-if="eventForm.hasConversation"
              type="button"
              class="self-start rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
              :class="
                eventForm.notifyClient !== false
                  ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                  : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
              "
              @click="eventForm.notifyClient = eventForm.notifyClient === false"
            >
              {{
                $t('CALENDAR.PARTICIPANTS.CLIENT', {
                  name:
                    eventForm.contactName ||
                    $t('CALENDAR.PARTICIPANTS.CLIENT_FALLBACK'),
                })
              }}
            </button>
            <button
              v-if="
                eventForm.hasConversation && eventForm.notifyClient !== false
              "
              type="button"
              class="self-start rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
              :class="
                eventForm.confirmationEnabled !== false
                  ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                  : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
              "
              @click="
                eventForm.confirmationEnabled =
                  eventForm.confirmationEnabled === false
              "
            >
              {{ $t('CALENDAR.PARTICIPANTS.REQUEST_CONFIRMATION') }}
            </button>
            <div
              v-for="(participant, index) in eventForm.participants"
              :key="index"
              class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3"
            >
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  v-model="participant.name"
                  type="text"
                  class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :placeholder="$t('CALENDAR.PARTICIPANTS.NAME')"
                />
                <input
                  v-model="participant.phone"
                  type="text"
                  class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :placeholder="$t('CALENDAR.PARTICIPANTS.PHONE')"
                />
                <input
                  v-model="participant.email"
                  type="email"
                  class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 !text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :placeholder="$t('CALENDAR.PARTICIPANTS.EMAIL')"
                />
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                  :class="
                    participant.via.includes('whatsapp')
                      ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                      : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="toggleParticipantVia(participant, 'whatsapp')"
                >
                  {{ $t('CALENDAR.PARTICIPANTS.VIA_WHATSAPP') }}
                </button>
                <button
                  type="button"
                  class="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                  :class="
                    participant.via.includes('email')
                      ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                      : 'border-n-weak text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="toggleParticipantVia(participant, 'email')"
                >
                  {{ $t('CALENDAR.PARTICIPANTS.VIA_EMAIL') }}
                </button>
                <span
                  v-if="participant.invited_at"
                  class="text-[11px] text-n-slate-10"
                >
                  {{ $t('CALENDAR.PARTICIPANTS.INVITED') }}
                </span>
                <button
                  type="button"
                  class="ml-auto i-lucide-trash-2 size-4 text-n-slate-10 hover:text-n-ruby-11"
                  :title="$t('CALENDAR.PARTICIPANTS.REMOVE')"
                  @click="removeParticipant(index)"
                />
              </div>
            </div>
          </div>

          <!-- Sincronizar com Google Calendar -->
          <label
            class="flex items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 cursor-pointer"
          >
            <span class="flex items-center gap-2 text-sm text-n-slate-11">
              <span class="i-lucide-refresh-cw size-4 text-n-slate-10" />
              {{ $t('CALENDAR.SYNC_WITH_GOOGLE') }}
            </span>
            <input
              v-model="eventForm.syncWithGoogle"
              type="checkbox"
              class="rounded border-n-weak"
            />
          </label>

          <!-- Reunião ChatSuite: anfitrião entra pelo host_url (grava/modera) -->
          <div
            v-if="!isNewEvent && eventForm.chatsuite_host_url"
            class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3"
          >
            <span
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
            >
              <span class="i-lucide-video size-4 text-n-teal-10" />
              {{ $t('CALENDAR.MEETING_SECTION_TITLE') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <NextButton
                color="teal"
                icon="i-lucide-video"
                @click="joinAsHost"
              >
                {{ $t('CALENDAR.JOIN_AS_HOST') }}
              </NextButton>
              <NextButton
                variant="outline"
                color="slate"
                icon="i-lucide-copy"
                @click="copyGuestLink"
              >
                {{ $t('CALENDAR.COPY_GUEST_LINK') }}
              </NextButton>
            </div>
            <span class="text-xs text-n-slate-10">
              {{ $t('CALENDAR.MEETING_SECTION_HINT') }}
            </span>
          </div>

          <div class="flex justify-end gap-2 pt-4 border-t border-n-weak">
            <NextButton
              variant="outline"
              color="slate"
              @click="closeEventModal"
            >
              {{ $t('CALENDAR.CANCEL') }}
            </NextButton>
            <NextButton v-if="!isNewEvent" color="ruby" @click="deleteEvent">
              {{ $t('CALENDAR.DELETE') }}
            </NextButton>
            <NextButton color="blue" :is-loading="isSaving" @click="saveEvent">
              {{ $t('CALENDAR.SAVE') }}
            </NextButton>
          </div>
        </div>
      </div>
    </WootModal>
  </section>
</template>

<style>
/*
 * FullCalendar renders its own DOM outside Vue's scoped styling reach, so it
 * can't be themed with Tailwind utility classes. These CSS custom properties
 * mirror our design tokens (see theme/colors.js and
 * app/javascript/dashboard/assets/scss/_next-colors.scss) so the calendar
 * matches the app's light/dark palette instead of hardcoded Tailwind greys.
 */

/* Calendar page full width */
.calendar-page {
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 auto !important;
}

/* Container and FullCalendar sizing */
.calendar-container {
  display: flex;
  flex-direction: column;
}

/* Light theme — mirrors :root tokens in _next-colors.scss (Google-like: subtle borders, white surfaces) */
.calendar-container .fc {
  flex: 1;
  width: 100%;
  height: 100%;
  --fc-border-color: #e6e9ee; /* n-border-weak */
  --fc-button-bg-color: #1f93ff; /* n-brand */
  --fc-button-border-color: #1f93ff;
  --fc-button-hover-bg-color: #0b6fd6; /* n-blue-10 */
  --fc-button-hover-border-color: #0b6fd6;
  --fc-button-active-bg-color: #0a5cb3;
  --fc-button-active-border-color: #0a5cb3;
  --fc-today-bg-color: rgba(31, 147, 255, 0.08);
  --fc-event-bg-color: #1f93ff; /* n-brand */
  --fc-event-border-color: #1f93ff; /* n-brand */
  --fc-page-bg-color: #f6f8fb; /* n-background */
  --fc-neutral-bg-color: #ffffff; /* n-solid-1 */
  --fc-list-event-hover-bg-color: #f1f3f6; /* n-solid-3 */
  --fc-non-business-color: transparent; /* sem sombreado de horário — grid uniforme (Google) */
}

.calendar-container .fc-theme-standard td,
.calendar-container .fc-theme-standard th {
  border-color: #e6e9ee; /* n-border-weak */
}

.calendar-container .fc-daygrid-day-number {
  color: #48566b; /* n-slate-11 */
  font-weight: 500;
  padding: 8px;
}

.calendar-container .fc-col-header-cell-cushion {
  color: #6b7a90; /* n-slate-10 */
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  padding: 12px 4px;
}

.calendar-container .fc-event {
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.8125rem;
  cursor: pointer;
}

.calendar-container .fc-event-title {
  font-weight: 500;
}

.calendar-container .fc-daygrid-day.fc-day-today {
  background-color: rgba(31, 147, 255, 0.08);
}

.calendar-container .fc-timegrid-slot-label {
  font-size: 0.75rem;
  color: #6b7a90; /* n-slate-10 */
}

.calendar-container .fc-scrollgrid {
  border-radius: 8px;
  overflow: hidden;
}

/* Dark theme — mirrors .dark tokens in _next-colors.scss */
.dark .calendar-container .fc {
  --fc-border-color: #1d2740; /* n-border-weak (dark) */
  --fc-button-bg-color: #1f93ff; /* n-brand */
  --fc-button-border-color: #1f93ff;
  --fc-button-hover-bg-color: #1a7fe0;
  --fc-button-hover-border-color: #1a7fe0;
  --fc-button-active-bg-color: #166bc4;
  --fc-button-active-border-color: #166bc4;
  --fc-today-bg-color: rgba(57, 163, 255, 0.07);
  --fc-event-bg-color: #1f93ff; /* n-brand */
  --fc-event-border-color: #1f93ff; /* n-brand */
  --fc-page-bg-color: #080b14; /* n-background (dark) */
  --fc-neutral-bg-color: #0e1422; /* n-solid-1 (dark) */
  --fc-list-event-hover-bg-color: #131b2e; /* n-solid-3 (dark) */
  --fc-non-business-color: transparent; /* sem sombreado de horário — grid uniforme (Google) */
}

.dark .calendar-container .fc-theme-standard td,
.dark .calendar-container .fc-theme-standard th {
  border-color: #1d2740; /* n-border-weak (dark) */
}

.dark .calendar-container .fc-daygrid-day-number {
  color: #aab6cf; /* n-slate-11 (dark) */
}

.dark .calendar-container .fc-col-header-cell-cushion {
  color: #7886a3; /* n-slate-10 (dark) */
}

.dark .calendar-container .fc-daygrid-day.fc-day-today {
  background-color: rgba(57, 163, 255, 0.07);
}

.dark .calendar-container .fc-timegrid-slot-label {
  color: #7886a3; /* n-slate-10 (dark) */
}

.dark .calendar-container .fc-day-other .fc-daygrid-day-number {
  color: #5b6884; /* n-slate-9 (dark) */
}

.dark .calendar-container .fc-scrollgrid {
  background-color: #080b14; /* n-background (dark) */
}

.dark .calendar-container .fc-list-day-cushion {
  background-color: #0e1422; /* n-solid-1 (dark) */
}

.dark .calendar-container .fc-list-event:hover td {
  background-color: #131b2e; /* n-solid-3 (dark) */
}
</style>
